import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {AuthService} from "../../../../shared-module/services/auth.service";
import {ActivatedRoute, Params, Router} from "@angular/router";
import Survey from "../../../../shared-module/model/survey.model";
import RelationContact from "../../../../shared-module/model/relation-contact.model";
import AnswerStore from "../../../../shared-module/services/Stores/answer.store";
import Answer from "../../../../shared-module/model/answer.model";
import {QuestionComponent} from "../../../../shared-module/components/question/question.component";
import Question from "../../../../shared-module/model/question.model";
import {ProgressBarComponent} from "../../../../shared-module/components/progress-bar/progress-bar.component";
import {CardComponent} from "../../../../shared-module/components/card/card.component";
import {ButtonComponent} from "../../../../shared-module/components/button/button.component";

import {forkJoin} from "rxjs";

@Component({
    selector: 'app-survey-questions',
    imports: [
    ReactiveFormsModule,
    QuestionComponent,
    ProgressBarComponent,
    CardComponent,
    ButtonComponent
],
    templateUrl: './questions.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './questions.component.scss'
})

export class SurveyQuestionsComponent implements OnInit {
  private readonly authService: AuthService = inject(AuthService);
  private readonly answerStore: AnswerStore = inject(AnswerStore);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);

  public form: FormGroup = new FormGroup({
    radio: new FormControl(null),
    checkbox: new FormControl(null),
    textarea: new FormControl(null)
  });
  protected survey: Survey | null = null;
  protected answers: Answer[] = [];
  protected questions: Question[] = [];
  protected currentQuestionIndex: number = 1;

  public ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      if (!params['id']) {
        return;
      }
      this.authService.getUser(['surveys.answers.question']).subscribe((user: RelationContact | null) => {
        if (user === null) {
          return;
        }
        const foundSurvey: Survey | null = user.surveys?.find((survey: Survey) => survey.id == params['id']) ?? null;
        if (foundSurvey === null) {
          return;
        }
        this.survey = foundSurvey;
        this.answerStore.reset().forParent("surveys", foundSurvey);

        this.answers = foundSurvey.answers ?? [];
        this.questions = foundSurvey.questions.sort((a: Question, b: Question) => a.position < b.position ? -1 : 1);

        if (!params['position']) {
          return;
        }

        this.currentQuestionIndex = this.getQuestionFromPosition(params['position']);

        this.checkAnswerAlreadyGivenAndRedirect();
      })
    });
  }

  public submit(): void {
    if (this.form.controls['radio'].hasError('required')) {
      this.form.setErrors({required: true});
      return;
    }
    if (this.form.invalid) {
      return;
    }

    const answersToSend: Answer[] = this.mapFormGroupToAnswers(this.form);

    forkJoin(
      answersToSend.map((answer: Answer) => {
        return this.answerStore.create(answer);
      })
    ).subscribe({
      complete: () => {
        this.gotoQuestion(++this.currentQuestionIndex);
      }
    });
    //reset the form for the next question
    this.form.reset({emitEvent: true});
  }

  public currentQuestionNumber(): number {
    return this.currentQuestionIndex + 1;
  }

  public currentPosition(): number {
    if (!this.questions[this.currentQuestionIndex]) {
      return -1;
    }
    return this.questions[this.currentQuestionIndex].position;
  }

  private getQuestionFromPosition(position: number): number {
    const foundQuestionIndex: number = this.questions.findIndex((question: Question) => question.position == position);
    if (foundQuestionIndex == -1) {
      //question not found, redirect to first question
      this.router.navigate(['/dashboard/surveys/', this.survey?.id, this.questions[0].position]);
      return -1;
    }
    return foundQuestionIndex;
  }

  private gotoQuestion(index: number) {
    if (!this.questions[index]) {
      //redirect to the last page
      this.router.navigate(['/dashboard/surveys/', this.survey?.id, 'thanks']);
      return;
    }
    //redirect to the new question
    this.router.navigate(['/dashboard/surveys/', this.survey?.id, this.questions[index].position]);
  }

  private checkAnswerAlreadyGivenAndRedirect(): void {
    if (this.answers.length === 0) {
      return;
    }
    const answerAlreadyGiven: boolean = this.answers.findIndex((answer: Answer) => answer.question?.position === this.questions[this.currentQuestionIndex].position) !== -1;

    if (answerAlreadyGiven) {
      this.gotoQuestion(++this.currentQuestionIndex);
    }
  }

  private mapFormGroupToAnswers(form: FormGroup): Answer[] {
    const regex: RegExp = /(<([^>]+)>)/ig;
    const data = form.getRawValue();
    const answers: Answer[] = [];

    if (data.radio != null) {
      answers.push(this.createAnswer(data.radio, null));
    }

    if (data.checkbox != null) {
      data.checkbox.forEach((choiceId: number) => {
        answers.push(this.createAnswer(choiceId, null));
      });
    }

    if (data.textarea != null) {
      const cleanedText = data.textarea.replace(regex, "");
      answers.push(this.createAnswer(null, cleanedText));
    }

    return answers;
  }

  private createAnswer(questionChoiceId: number | null, text: string | null): Answer {
    const answer = new Answer();
    answer.position = this.currentPosition();
    answer.question_choice_id = questionChoiceId;
    answer.text = text;
    return answer;
  }
}
